import { createSlice, isAnyOf } from '@reduxjs/toolkit';

import {
  importDataset,
  initSonarProject,
  editCreateSonarProject,
  addDataset,
  crawlSingleMedia,
  crawlAllMedia,
  deleteFromDataset,
  editDataset,
} from './createSonarActions';
import joinDatasets from '../../utils/joinDatasets';

const initialState = {
  dataset: [],
  isDatasetLoading: false,
  initLoading: false,
  projectInfo: {},
  isAllDatasetSelected: false,

  datasetErrors: {},

  groupsOptions: [],
  categoriesOptions: [],
  productsOptions: [],
  servicesOptions: [],

  isCrawling: false,
  crawledUrls: {},
};

const scoutSettings = createSlice({
  name: 'createSonar',
  initialState,
  reducers: {
    addNewDataset: (state, action) => {
      state.dataset = joinDatasets(
        state.dataset,
        [{ isSelected: state.isAllDatasetSelected, ...action.payload }],
        'companyName',
      );
    },

    selectAllDatasets: (state) => {
      state.dataset = state.dataset.map((item) => ({ ...item, isSelected: !state.isAllDatasetSelected }));
      state.isAllDatasetSelected = !state.isAllDatasetSelected;
    },
    selectOneDataset: (state, action) => {
      state.dataset = state.dataset.map((item) =>
        item.id === action.payload ? { ...item, isSelected: !item.isSelected } : item,
      );
    },

    addNewCategoryOption: (state, action) => {
      state.categoriesOptions = [...state.categoriesOptions, action.payload];
    },
    addNewGroupOption: (state, action) => {
      state.groupsOptions = [...state.groupsOptions, action.payload];
    },
    addNewServiceOption: (state, action) => {
      state.servicesOptions = [...state.servicesOptions, action.payload];
    },
    addNewProductOption: (state, action) => {
      state.productsOptions = [...state.productsOptions, action.payload];
    },
    clearCrawledUrls: (state) => {
      state.crawledUrls = {};
    },

    resetCreateSonarData: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      .addCase(initSonarProject.fulfilled, (state, action) => {
        state.initLoading = false;
        state.projectInfo = action.payload.projectInfo;

        const formattedDataset = action.payload.dataset.map((item) => ({
          ...item,
          isSelected: false,
        }));
        state.dataset = formattedDataset;
      })
      .addCase(initSonarProject.pending, (state) => {
        state.initLoading = true;
      })
      .addCase(initSonarProject.rejected, (state) => {
        state.initLoading = false;
        state.projectInfo = {};
      })
      .addCase(importDataset.rejected, (state, action) => {
        state.datasetErrors = action.payload;
        state.isDatasetLoading = false;
      })
      .addCase(importDataset.pending, (state) => {
        state.isDatasetLoading = true;
      })
      .addCase(importDataset.fulfilled, (state, action) => {
        const formattedDataset = action.payload.map((item) => ({ ...item, isSelected: false }));

        state.dataset = [...formattedDataset, ...state.dataset];
        state.isDatasetLoading = false;
        state.datasetErrors = {};
      })

      .addCase(addDataset.fulfilled, (state, action) => {
        state.isDatasetLoading = false;
        state.dataset = [{ ...action.payload, isSelected: false }, ...state.dataset];
      })
      .addCase(addDataset.pending, (state) => {
        state.isDatasetLoading = true;
      })
      .addCase(addDataset.rejected, (state) => {
        state.isDatasetLoading = false;
      })

      .addCase(editDataset.fulfilled, (state, action) => {
        state.isDatasetLoading = false;
        state.dataset = state.dataset.map((item) =>
          item.id === action.payload.id ? { ...action.payload, isSelected: item.isSelected } : item,
        );
      })
      .addCase(editDataset.pending, (state) => {
        state.isDatasetLoading = true;
      })
      .addCase(editDataset.rejected, (state) => {
        state.isDatasetLoading = false;
      })

      .addCase(deleteFromDataset.rejected, (state) => {
        state.isDatasetLoading = false;
      })
      .addCase(deleteFromDataset.pending, (state) => {
        state.isDatasetLoading = true;
      })
      .addCase(deleteFromDataset.fulfilled, (state, action) => {
        state.dataset = state.dataset.filter(({ id }) => !action.payload.includes(id));
        state.isDatasetLoading = false;
      })

      .addCase(editCreateSonarProject.fulfilled, (state, action) => {
        state.projectInfo = action.payload;
      })
      .addMatcher(isAnyOf(crawlSingleMedia.fulfilled, crawlAllMedia.fulfilled), (state, action) => {
        state.isCrawling = false;
        const updatedUrls = { ...state.crawledUrls, ...action.payload };

        state.crawledUrls = updatedUrls;
      })
      .addMatcher(isAnyOf(crawlSingleMedia.pending, crawlAllMedia.pending), (state) => {
        state.isCrawling = true;
      })
      .addMatcher(isAnyOf(crawlSingleMedia.rejected, crawlAllMedia.rejected), (state) => {
        state.isCrawling = false;
      });
  },
});

export const {
  addNewDataset,
  resetCreateSonarData,

  selectAllDatasets,
  selectOneDataset,

  addNewCategoryOption,
  addNewGroupOption,
  addNewServiceOption,
  addNewProductOption,
  clearCrawledUrls,
} = scoutSettings.actions;

export default scoutSettings.reducer;
